/** @jsxImportSource @emotion/react */
import React, { useState, useImperativeHandle, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { SetupStep, SetupStepCondition } from 'models/setup/SetupStep';
import { ContentSection } from 'components/layouts/ContentSection';
import { ContentFlex } from 'components/layouts/ContentFlex';
import StepTypeGrid from './StepTypeGrid';
import CommonStepGrid from './CommonStepGrid';
import { GlobalBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { useLoading } from 'components/process/Loading';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 프로젝트 셋업단계 설정/조회 > 프로젝트 셋업단계 설정 > [단계 유형/공통단계] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.07.07   jshwang12       신규 생성
 * 2026.08.07   jshwang12       설비가 등록된 경우 단계 수정 불가 (가중치 포함)
 * 2026.08.25   jshwang12       단계 유형/공통단계 저장 수정 (개별 저장 -> 통합 저장), 조직유형코드 추가
 */

interface Props {
  elmPrjId?: string;
  isReadOnly?: boolean; // 읽기만 가능여부 (true인 경우 수정권한이 아예 없는 상태)
  orgTpCd?: string; // 조직유형코드
  setupStatus?: SetupStep[]; // 단계유형별 설정가능 상태정보
  onSaved?: () => void; // 저장 완료 (상위에서 상태정보 재조회)
}

const CommonStepContent = (
  { isReadOnly = true, orgTpCd, setupStatus = [], onSaved, ...props }: Props,
  ref
) => {
  const stepRef = useRef<any>();
  const cmnRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const { openLoading, isLoading } = useLoading();
  const [condition, setCondition] = useState<SetupStepCondition>();

  useImperativeHandle(ref, () => ({
    search: (params?: SetupStepCondition) => {
      setCondition(params);
      stepRef.current?.search(params);
      cmnRef.current?.search(params);
    },
    // 상위 컴포넌트의 설정완료 처리에서 사용
    hasChanges: () => getSaveTargets().length > 0,
    saveChanges: () => saveChanges(),
  }));

  /**
   * 저장 대상 (수정 가능하고 변경사항이 있는 그리드)
   */
  const getSaveTargets = () => {
    const targets = [] as any[];
    if (!stepTypeReadOnly && stepRef.current?.hasChanges()) {
      targets.push(stepRef);
    }
    if (!commonReadOnly && cmnRef.current?.hasChanges()) {
      targets.push(cmnRef);
    }
    return targets;
  };

  /**
   * 저장 대상을 단계유형 -> 공통단계 순으로 순차 저장 (실패 시 해당 시점에 중단)
   */
  const saveTargets = async (targets: any[]) => {
    const savedTargets = [] as any[];
    for (const target of targets) {
      const res = await target.current?.save();
      if (res?.successOrNot !== 'Y') {
        openMessageBar({
          type: 'error',
          content:
            res?.data && typeof res.data === 'string'
              ? res.data
              : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
        });
        // 저장에 성공한 대상만 재조회 (실패한 대상은 수정내용 유지)
        savedTargets.forEach((o) => o.current?.refresh());
        if (savedTargets.length > 0) {
          onSaved?.();
        }
        return false;
      }
      savedTargets.push(target);
    }
    savedTargets.forEach((o) => o.current?.refresh());
    // 단계유형이 추가/삭제되었을 수 있으므로 상위의 상태정보도 갱신
    onSaved?.();
    return true;
  };

  /**
   * 검증 후 저장 (확인 모달 없이 처리하며 성공여부를 반환)
   * 상위 컴포넌트의 설정완료 처리에서 사용
   */
  const saveChanges = async () => {
    const targets = getSaveTargets();
    if (targets.length < 1) {
      return true;
    }
    if (targets.some((o) => !o.current?.validate())) {
      return false;
    }
    return await saveTargets(targets);
  };

  // 프로젝트가 완료된 경우 단계유형 수정 불가
  const stepTypeReadOnly = useMemo(() => {
    if (!(setupStatus || []).length) return true;
    return isReadOnly || setupStatus[0]?.prjCpltYn !== 'N';
  }, [isReadOnly, setupStatus]);

  // 단계설정이 완료된 경우 공통단계 수정 불가
  const commonReadOnly = useMemo(() => {
    if (!(setupStatus || []).length) return true;
    return isReadOnly || setupStatus[0]?.setgCpltYn !== 'N';
  }, [isReadOnly, setupStatus]);

  /**
   * 단계유형/공통단계 통합 저장
   * 수정 가능하고 변경사항이 있는 그리드만 저장 대상으로 처리
   */
  const handleSave = () => {
    const targets = getSaveTargets();
    if (targets.length < 1) {
      return;
    }
    // 각 그리드의 유효성 검증 (실패 시 해당 그리드에서 메시지 노출)
    if (targets.some((o) => !o.current?.validate())) {
      return;
    }
    const msg = targets.reduce(
      (acc, cur) => [...acc, ...(cur.current?.getConfirmMessages?.() || [])],
      [] as string[]
    );
    msg.push(t('com.label.저장하시겠습니까?', '저장하시겠습니까?'));

    openCommonModal({
      modalType: 'confirm',
      content: msg.join('<br/>'),
      yesCallback: async () => {
        openLoading(true);
        try {
          // 실패 시 메시지는 saveTargets에서 노출
          if (await saveTargets(targets)) {
            openMessageBar({
              type: 'confirm',
              content: t('com.label.저장되었습니다.', '저장되었습니다.'),
            });
          }
        } finally {
          openLoading(false);
        }
      },
    });
  };

  return (
    <>
      <ContentFlex>
        <ContentSection className="section marginT0 width45p">
          <StepTypeGrid
            ref={stepRef}
            isReadOnly={stepTypeReadOnly}
            orgTpCd={orgTpCd}
            setupStatus={setupStatus}
          />
        </ContentSection>
        <ContentSection className="section width50p">
          <CommonStepGrid ref={cmnRef} isReadOnly={commonReadOnly} />
        </ContentSection>
      </ContentFlex>
      {(!stepTypeReadOnly || !commonReadOnly) && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={handleSave}>
            {t('com.button.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default React.forwardRef(CommonStepContent);
